// Things players point with, and a few table-wide lights:
//   - attack arrows (Alt + drag from a card; a click on your own arrow takes it away)
//   - pings (Alt + click)
//   - the pulse that runs between an Emperor and its Minion when damage is shared
//   - the embers that drift up around the table during Overtime
// How long arrows and pings last is in src/config.ts (pointers); colors come from the caller.
import * as THREE from 'three';
import { fxTextures } from './effects.ts';

const SEGMENTS = 28;
const HEAD_LENGTH = 5.6;
const HEAD_WIDTH = 5.4;
const SHAFT_WIDTH = 2.1;

function canvasTexture(w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

let shared: { chevrons: THREE.Texture; band: THREE.Texture; solid: THREE.Texture; ring: THREE.Texture; pin: THREE.Texture } | null = null;
function textures() {
  if (shared) return shared;
  // a strip of chevrons pointing along the arrow; it scrolls so the arrow seems to flow toward its target
  const chevrons = canvasTexture(64, 64, (ctx) => {
    // the body of the arrow is a slightly darker shade of its color; the chevrons are the full, bright color
    ctx.fillStyle = 'rgb(168,168,168)';
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.moveTo(14, 8);
    ctx.lineTo(34, 8);
    ctx.lineTo(54, 32);
    ctx.lineTo(34, 56);
    ctx.lineTo(14, 56);
    ctx.lineTo(34, 32);
    ctx.closePath();
    ctx.fill();
  });
  chevrons.wrapS = THREE.RepeatWrapping;
  // a soft band: bright along the middle, fading to nothing at both edges
  const band = canvasTexture(8, 64, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 0, 64);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.5, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 8, 64);
  });
  // a soft-edged solid strip, for the dark outline under an arrow
  const solid = canvasTexture(8, 64, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 0, 64);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.2, 'rgba(255,255,255,1)');
    g.addColorStop(0.8, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 8, 64);
  });
  // a bold ring with a dark edge, so a ping reads on the bright mat as well as on the dark table
  const ring = canvasTexture(256, 256, (ctx) => {
    ctx.beginPath();
    ctx.arc(128, 128, 104, 0, Math.PI * 2);
    ctx.lineWidth = 30;
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.stroke();
    ctx.lineWidth = 17;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
  });
  // the marker that hovers over a pinged spot: a pointer aimed straight down
  const pin = canvasTexture(96, 128, (ctx) => {
    ctx.beginPath();
    ctx.moveTo(48, 122);
    ctx.lineTo(10, 46);
    ctx.arc(48, 46, 38, Math.PI, 0);
    ctx.closePath();
    ctx.lineJoin = 'round';
    ctx.lineWidth = 12;
    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.beginPath();
    ctx.arc(48, 46, 14, 0, Math.PI * 2);
    ctx.fill();
  });
  shared = { chevrons, band, solid, ring, pin };
  return shared;
}

const tmpA = new THREE.Vector3();

/** A flat ribbon that follows an arc from one spot to another, drawn up to `upTo` (0–1) of the way. */
class Ribbon {
  readonly mesh: THREE.Mesh;
  private pos: Float32Array;
  private uv: Float32Array;
  private widthScale: number;

  constructor(material: THREE.Material, widthScale = 1) {
    this.widthScale = widthScale;
    const geo = new THREE.BufferGeometry();
    this.pos = new Float32Array((SEGMENTS + 1) * 2 * 3);
    this.uv = new Float32Array((SEGMENTS + 1) * 2 * 2);
    const index: number[] = [];
    for (let i = 0; i < SEGMENTS; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(this.uv, 2));
    geo.setIndex(index);
    this.mesh = new THREE.Mesh(geo, material);
    this.mesh.frustumCulled = false;
  }

  /** `stop` = how far along the arc the ribbon ends (the arrowhead takes over from there). */
  shape(arc: Arc, upTo: number, stop: number, width: number) {
    const end = Math.max(0, Math.min(upTo, stop));
    for (let i = 0; i <= SEGMENTS; i++) {
      const t = (i / SEGMENTS) * end;
      arc.at(t, tmpA);
      const w = (width * this.widthScale * (0.45 + 0.55 * Math.min(1, t * 3))) / 2; // slim where it leaves the card
      const o = i * 6;
      this.pos[o] = tmpA.x + arc.side.x * w;
      this.pos[o + 1] = tmpA.y;
      this.pos[o + 2] = tmpA.z + arc.side.z * w;
      this.pos[o + 3] = tmpA.x - arc.side.x * w;
      this.pos[o + 4] = tmpA.y;
      this.pos[o + 5] = tmpA.z - arc.side.z * w;
      const u = (t * arc.length) / 3.2;
      this.uv.set([u, 0, u, 1], i * 4);
    }
    const g = this.mesh.geometry;
    g.attributes.position.needsUpdate = true;
    g.attributes.uv.needsUpdate = true;
  }

  dispose() {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

/** The path an arrow takes: up off the table in a gentle arc and down onto its target. */
class Arc {
  from = new THREE.Vector3();
  to = new THREE.Vector3();
  private mid = new THREE.Vector3();
  side = new THREE.Vector3(1, 0, 0); // the horizontal direction across the arrow
  length = 1;

  set(from: THREE.Vector3, to: THREE.Vector3, lift = 1) {
    this.from.copy(from);
    this.to.copy(to);
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    this.length = Math.max(0.001, Math.hypot(dx, dz));
    this.side.set(-dz / this.length, 0, dx / this.length);
    const rise = lift * Math.min(9, 1.2 + this.length * 0.13);
    this.mid.set((from.x + to.x) / 2, Math.max(from.y, to.y) + rise * 2, (from.z + to.z) / 2);
    return this;
  }

  at(t: number, out: THREE.Vector3) {
    const a = (1 - t) * (1 - t);
    const b = 2 * (1 - t) * t;
    const c = t * t;
    return out.set(
      a * this.from.x + b * this.mid.x + c * this.to.x,
      a * this.from.y + b * this.mid.y + c * this.to.y,
      a * this.from.z + b * this.mid.z + c * this.to.z,
    );
  }
}

class Arrow {
  readonly group = new THREE.Group();
  private arc = new Arc();
  private core: Ribbon;
  private glow: Ribbon;
  private edge: Ribbon; // a dark outline, so the arrow stands out on any background
  private headEdge: THREE.Mesh;
  private head: THREE.Mesh;
  private headGlow: THREE.Mesh;
  private ring: THREE.Mesh;
  private mats: THREE.MeshBasicMaterial[] = [];
  born = 0;
  life = Infinity; // seconds; a preview arrow lives until it is taken away
  key = 0; // the number of the attack this arrow shows (0 for a preview)
  mine = false; // you drew it: a click on it takes it away
  private drawn = -1;

  /**
   * Is a point on the screen on this arrow? `project` turns a spot in the scene into screen pixels.
   * The arrow is followed along its arc, as wide as it looks from here plus a few pixels of slack.
   */
  hit(project: (v: THREE.Vector3) => { x: number; y: number }, px: number, py: number) {
    const arc = this.arc;
    const upTo = Math.max(0, this.drawn);
    if (upTo <= 0.05) return false;
    const headT = Math.min(0.6, HEAD_LENGTH / arc.length);
    const steps = 16;
    let prev: { x: number; y: number; r: number } | null = null;
    const at = new THREE.Vector3();
    const edge = new THREE.Vector3();
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * upTo;
      arc.at(t, at);
      // the shaft, then the wider arrowhead, which narrows to its tip
      const inHead = t > upTo - headT;
      const half = inHead ? Math.max(SHAFT_WIDTH / 2, (HEAD_WIDTH / 2) * Math.min(1, arc.length / 6) * ((upTo - t) / headT)) : SHAFT_WIDTH / 2;
      edge.copy(at).addScaledVector(arc.side, half);
      const c = project(at);
      const e = project(edge);
      const cur = { x: c.x, y: c.y, r: Math.hypot(e.x - c.x, e.y - c.y) + 5 };
      if (prev) {
        // distance from the point to this stretch of the arrow
        const dx = cur.x - prev.x;
        const dy = cur.y - prev.y;
        const len2 = dx * dx + dy * dy || 1;
        const k = Math.max(0, Math.min(1, ((px - prev.x) * dx + (py - prev.y) * dy) / len2));
        if (Math.hypot(px - (prev.x + dx * k), py - (prev.y + dy * k)) <= Math.max(prev.r, cur.r)) return true;
      }
      prev = cur;
    }
    return false;
  }

  constructor(color: string) {
    const t = textures();
    const tint = new THREE.Color(color);
    const bright = tint.clone().lerp(new THREE.Color('#ffffff'), 0.22);
    const dark = new THREE.Color('#07080a');
    const mat = (map: THREE.Texture | null, c: THREE.Color, additive: boolean) => {
      const m = new THREE.MeshBasicMaterial({
        map,
        color: c,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
        blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      });
      this.mats.push(m);
      return m;
    };
    this.glow = new Ribbon(mat(t.band, tint, true), 3.2);
    this.core = new Ribbon(mat(t.chevrons, bright, false));
    const headGeo = new THREE.BufferGeometry();
    headGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
    this.head = new THREE.Mesh(headGeo, mat(null, bright, false));
    const glowGeo = new THREE.PlaneGeometry(1, 1);
    glowGeo.rotateX(-Math.PI / 2);
    this.headGlow = new THREE.Mesh(glowGeo, mat(fxTextures().dot, tint, true));
    this.ring = new THREE.Mesh(glowGeo.clone(), mat(fxTextures().circle, tint, true));
    this.edge = new Ribbon(mat(t.solid, dark, false), 1.5);
    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
    this.headEdge = new THREE.Mesh(edgeGeo, mat(null, dark, false));
    // drawn back to front: glow, dark outline, then the arrow itself
    [this.glow.mesh, this.ring, this.edge.mesh, this.headEdge, this.core.mesh, this.head, this.headGlow].forEach((m, i) => {
      m.frustumCulled = false;
      m.renderOrder = 20 + i;
      this.group.add(m);
    });
  }

  aim(from: THREE.Vector3, to: THREE.Vector3) {
    this.arc.set(from, to);
    this.drawn = -1;
  }

  /** Returns false once the arrow has faded away. */
  update(time: number) {
    const age = time - this.born;
    if (age > this.life) return false;
    const reach = Math.min(1, age / 0.22);
    const upTo = 1 - Math.pow(1 - reach, 3); // the arrow shoots out from the card
    if (upTo !== this.drawn) {
      this.drawn = upTo;
      const arc = this.arc;
      const headT = Math.min(0.6, HEAD_LENGTH / arc.length);
      const stop = 1 - headT;
      this.glow.shape(arc, upTo, 1, SHAFT_WIDTH);
      this.edge.shape(arc, upTo, stop + 0.01, SHAFT_WIDTH);
      this.core.shape(arc, upTo, stop + 0.01, SHAFT_WIDTH);
      // the arrowhead rides the front of the arrow
      const tip = arc.at(upTo, new THREE.Vector3());
      const base = arc.at(Math.max(0, upTo - headT), new THREE.Vector3());
      const w = (HEAD_WIDTH / 2) * Math.min(1, arc.length / 6);
      const p = this.head.geometry.attributes.position as THREE.BufferAttribute;
      p.setXYZ(0, base.x + arc.side.x * w, base.y, base.z + arc.side.z * w);
      p.setXYZ(1, base.x - arc.side.x * w, base.y, base.z - arc.side.z * w);
      p.setXYZ(2, tip.x, tip.y, tip.z);
      p.needsUpdate = true;
      // the same triangle, a little larger, in the outline color
      const along = tip.clone().sub(base).normalize();
      const e = this.headEdge.geometry.attributes.position as THREE.BufferAttribute;
      const grow = 0.75;
      e.setXYZ(0, base.x + arc.side.x * (w + grow * 1.4) - along.x * grow, base.y - along.y * grow, base.z + arc.side.z * (w + grow * 1.4) - along.z * grow);
      e.setXYZ(1, base.x - arc.side.x * (w + grow * 1.4) - along.x * grow, base.y - along.y * grow, base.z - arc.side.z * (w + grow * 1.4) - along.z * grow);
      e.setXYZ(2, tip.x + along.x * grow * 1.6, tip.y + along.y * grow * 1.6, tip.z + along.z * grow * 1.6);
      e.needsUpdate = true;
      this.headGlow.position.copy(tip).lerp(base, 0.4);
      this.ring.position.copy(arc.to);
      this.ring.position.y += 0.05;
    }
    const fade = Number.isFinite(this.life) ? Math.max(0, Math.min(1, (this.life - age) / 0.6)) : 1;
    const beat = 0.5 + 0.5 * Math.sin(time * 6);
    this.mats[0].opacity = 0.6 * fade; // glow
    this.mats[1].opacity = fade; // chevrons
    this.mats[2].opacity = fade; // head
    this.mats[3].opacity = (0.5 + 0.3 * beat) * fade; // glow on the tip
    this.mats[4].opacity = (0.55 + 0.4 * beat) * fade * reach; // ring on the target
    this.mats[5].opacity = 0.62 * fade; // outline
    this.mats[6].opacity = 0.62 * fade;
    this.headGlow.scale.setScalar(8);
    const ring = 6.2 + 1.3 * beat;
    this.ring.scale.set(ring, 1, ring);
    return true;
  }

  dispose() {
    this.group.removeFromParent();
    this.core.dispose();
    this.glow.dispose();
    this.edge.dispose();
    for (const m of [this.head, this.headEdge, this.headGlow, this.ring]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  }
}

interface Timed {
  group: THREE.Group;
  born: number;
  life: number;
  update(k: number, time: number): void;
}

const EMBERS = 170;

export class Markers {
  private scene: THREE.Scene;
  private arrows: Arrow[] = [];
  private preview: Arrow | null = null;
  private previewColor = '';
  private timed: Timed[] = [];
  private embers: THREE.Points | null = null;
  private emberSeeds: { x: number; z: number; speed: number; sway: number; phase: number; top: number }[] = [];
  private mood = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  // ---------------- attack arrows ----------------

  /** The arrow you see while you are still dragging (only you see this one). Pass null to take it away. */
  setPreview(from: THREE.Vector3 | null, to?: THREE.Vector3, color = '#ffffff') {
    if (!from || !to) {
      this.preview?.dispose();
      this.preview = null;
      return;
    }
    if (!this.preview || this.previewColor !== color) {
      this.preview?.dispose();
      this.preview = new Arrow(color);
      this.preview.born = performance.now() / 1000 - 1; // already fully drawn
      this.previewColor = color;
      this.scene.add(this.preview.group);
    }
    this.preview.aim(from, to);
  }

  /** A declared attack: the arrow shoots from the attacker to its target and fades after `seconds`. */
  arrow(from: THREE.Vector3, to: THREE.Vector3, color: string, seconds: number, key = 0, mine = false) {
    const a = new Arrow(color);
    a.born = performance.now() / 1000;
    a.life = seconds;
    a.key = key;
    a.mine = mine;
    a.aim(from, to);
    this.scene.add(a.group);
    this.arrows.push(a);
    while (this.arrows.length > 8) this.arrows.shift()!.dispose();
  }

  /** The arrow you drew that is under a point on the screen (its number), or null. The newest one wins. */
  arrowAt(camera: THREE.Camera, rect: { left: number; top: number; width: number; height: number }, clientX: number, clientY: number): number | null {
    if (!this.arrows.some((a) => a.mine)) return null;
    const v = new THREE.Vector3();
    const project = (p: THREE.Vector3) => {
      v.copy(p).project(camera);
      return { x: rect.left + ((v.x + 1) / 2) * rect.width, y: rect.top + ((1 - v.y) / 2) * rect.height };
    };
    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const a = this.arrows[i];
      if (a.mine && a.key && a.hit(project, clientX, clientY)) return a.key;
    }
    return null;
  }

  /** Take an arrow away at once (its owner clicked it). */
  endArrow(key: number) {
    this.arrows = this.arrows.filter((a) => {
      if (a.key !== key) return true;
      a.dispose();
      return false;
    });
  }

  // ---------------- pings ----------------

  /** "Look here": bold rings spreading from a spot on the table, under a marker that hovers above it. */
  ping(at: THREE.Vector3, color: string, seconds: number) {
    const t = fxTextures();
    const own = textures();
    const group = new THREE.Group();
    group.position.copy(at);
    const tint = new THREE.Color(color).lerp(new THREE.Color('#ffffff'), 0.18);
    const mat = (map: THREE.Texture, additive: boolean) =>
      new THREE.MeshBasicMaterial({ map, color: additive ? color : tint, transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
    const plane = new THREE.PlaneGeometry(1, 1);
    plane.rotateX(-Math.PI / 2);
    const rings = [0, 1, 2].map(() => new THREE.Mesh(plane, mat(own.ring, false)));
    const glow = new THREE.Mesh(plane, mat(t.dot, true));
    const sprite = (map: THREE.Texture, additive: boolean) =>
      new THREE.Sprite(new THREE.SpriteMaterial({ map, color: additive ? color : tint, transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending }));
    const beam = sprite(t.column, true);
    beam.center.set(0.5, 0);
    const pin = sprite(own.pin, false);
    pin.center.set(0.5, 0);
    [glow, ...rings, beam, pin].forEach((m, i) => {
      if (m instanceof THREE.Mesh) m.position.y = 0.3;
      m.renderOrder = 30 + i;
    });
    group.add(glow, ...rings, beam, pin);
    this.scene.add(group);
    this.timed.push({
      group,
      born: performance.now() / 1000,
      life: seconds,
      update: (k) => {
        const fade = Math.min(1, (1 - k) * 4);
        const drop = 1 - Math.pow(1 - Math.min(1, k * 7), 3); // the marker drops in, then bobs
        rings.forEach((r, i) => {
          // each ring goes out twice over the life of the ping, one after the other
          const local = (k * 2 + 1 - i * 0.22) % 1;
          const size = 2 + 13 * (1 - Math.pow(1 - local, 2));
          r.scale.set(size, 1, size);
          (r.material as THREE.MeshBasicMaterial).opacity = k * 2 < i * 0.22 ? 0 : Math.pow(1 - local, 1.2) * fade;
        });
        glow.scale.set(9, 1, 9);
        (glow.material as THREE.MeshBasicMaterial).opacity = 0.7 * fade;
        beam.scale.set(4, 12 * drop, 1);
        beam.material.opacity = 0.6 * fade;
        pin.position.y = 1.2 + (1 - drop) * 9 + 0.5 * Math.sin(k * seconds * 7);
        pin.scale.set(3.3, 4.4, 1);
        pin.material.opacity = fade;
      },
    });
  }

  // ---------------- the Emperor–Minion link ----------------

  /** A pulse of light running from one card to another along the table, taking `travel` seconds. */
  link(from: THREE.Vector3, to: THREE.Vector3, colors: string[], travel: number) {
    const t = fxTextures();
    const group = new THREE.Group();
    const a = from.clone().setY(0.45);
    const b = to.clone().setY(0.45);
    const arc = new Arc().set(a, b, 0);
    const lineMat = new THREE.MeshBasicMaterial({ map: textures().band, color: colors[0], transparent: true, opacity: 0, depthWrite: false, depthTest: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    const line = new Ribbon(lineMat, 1);
    line.shape(arc, 1, 1, 2.6);
    line.mesh.renderOrder = 19;
    group.add(line.mesh);
    const orbs = Array.from({ length: 9 }, (_, i) => {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: i === 0 ? (colors[1] ?? colors[0]) : colors[0], transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending }));
      sp.renderOrder = 21;
      group.add(sp);
      return sp;
    });
    this.scene.add(group);
    const life = travel + 0.4;
    this.timed.push({
      group,
      born: performance.now() / 1000,
      life,
      update: (k) => {
        const u = Math.min(1, (k * life) / travel); // how far the pulse has run
        const tail = k * life > travel ? 1 - (k * life - travel) / 0.4 : 1;
        lineMat.opacity = 0.8 * Math.sin(Math.PI * Math.min(1, k * 1.05)) ** 0.6;
        orbs.forEach((sp, i) => {
          const at = Math.max(0, u - i * 0.035);
          arc.at(at, sp.position);
          sp.position.y += 0.35;
          const size = (i === 0 ? 4.6 : 3.4) * (1 - i / 11);
          sp.scale.set(size, size, 1);
          sp.material.opacity = (1 - i / 10) * tail;
        });
      },
    });
  }

  /**
   * Gerzite flying from a bar into a card, one after another (Gerzite Dagger, Crystalline Discharge): small
   * glowing crystals arc across, each with a short trail, and flare as they sink in. All of them have landed by
   * `travel` seconds.
   */
  stream(from: THREE.Vector3, to: THREE.Vector3, colors: string[], count: number, travel: number) {
    const t = fxTextures();
    const group = new THREE.Group();
    const gap = count > 1 ? (travel * 0.45) / (count - 1) : 0;
    const flight = travel * 0.55;
    const bits = Array.from({ length: count }, (_, i) => {
      const arc = new Arc().set(from.clone().add(new THREE.Vector3((Math.random() - 0.5) * 4, 0, (Math.random() - 0.5) * 4)), to.clone(), 0.9 + Math.random() * 0.4);
      const trail = Array.from({ length: 4 }, (_, j) => {
        const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: j === 0 ? (colors[1] ?? colors[0]) : colors[0], transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending }));
        sp.renderOrder = 22;
        group.add(sp);
        return sp;
      });
      return { arc, trail, start: i * gap };
    });
    this.scene.add(group);
    const life = travel + 0.3;
    const p = new THREE.Vector3();
    this.timed.push({
      group,
      born: performance.now() / 1000,
      life,
      update: (k) => {
        const time = k * life;
        for (const b of bits) {
          const u = (time - b.start) / flight;
          b.trail.forEach((sp, j) => {
            const m = sp.material as THREE.SpriteMaterial;
            const at = u - j * 0.06;
            if (at <= 0 || at >= 1.15) { m.opacity = 0; return; }
            b.arc.at(Math.min(1, at), p);
            sp.position.copy(p);
            const flare = at >= 1 ? 1 + (at - 1) * 12 : 1; // (it flares as it sinks into the card)
            const size = (j === 0 ? 2.2 : 1.6 * (1 - j / 5)) * flare;
            sp.scale.set(size, size, 1);
            m.opacity = (1 - j / 4) * (at >= 1 ? 1 - (at - 1) / 0.15 : 1);
          });
        }
      },
    });
  }

  /**
   * A gust of wind and snow blowing from one card to another (Summit Tarragon's discount reaching the Frigid card
   * played for less): pale wind streaks race along a low arc, and snowflakes tumble along it, swirling round the
   * path, then scatter at the far end. colors: [wind, snow, glint].
   */
  gust(from: THREE.Vector3, to: THREE.Vector3, colors: string[], travel: number) {
    const t = fxTextures();
    const group = new THREE.Group();
    const arc = new Arc().set(from.clone().setY(1.5), to.clone().setY(1.5), 0.55);
    // wind: long thin streaks running along the path one after another
    const streaks = Array.from({ length: 7 }, (_, i) => {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t.trail, color: colors[0], transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending }));
      sp.renderOrder = 20;
      group.add(sp);
      return { sp, start: i * 0.07, off: (Math.random() - 0.5) * 2.4, lift: Math.random() * 1.2 };
    });
    // snow: flakes riding the gust, each swirling round the path at its own speed
    const flakes = Array.from({ length: 34 }, () => {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t.dot, color: Math.random() < 0.25 ? (colors[2] ?? colors[1]) : (colors[1] ?? colors[0]), transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending }));
      sp.renderOrder = 21;
      group.add(sp);
      return { sp, start: Math.random() * 0.45, speed: 0.8 + Math.random() * 0.5, r: 0.6 + Math.random() * 2.2, spin: 6 + Math.random() * 8, phase: Math.random() * 6.28, size: 1.1 + Math.random() * 1.4 };
    });
    this.scene.add(group);
    const life = travel + 0.6;
    const p = new THREE.Vector3();
    const dir = new THREE.Vector3(to.x - from.x, 0, to.z - from.z);
    const angle = Math.atan2(-dir.z, dir.x); // (a streak lies along the way the wind blows, roughly, from above)
    this.timed.push({
      group,
      born: performance.now() / 1000,
      life,
      update: (k) => {
        const time = k * life;
        for (const st of streaks) {
          const u = (time - st.start) / (travel * 0.75);
          const m = st.sp.material as THREE.SpriteMaterial;
          if (u <= 0 || u >= 1) { m.opacity = 0; continue; }
          arc.at(u, p);
          st.sp.position.set(p.x + arc.side.x * st.off, p.y + st.lift, p.z + arc.side.z * st.off);
          st.sp.scale.set(9 * Math.sin(Math.PI * u) + 1.5, 1.1, 1);
          m.rotation = angle;
          m.opacity = 0.75 * Math.sin(Math.PI * u);
        }
        for (const f of flakes) {
          const u = (time - f.start) / (travel * f.speed);
          const m = f.sp.material as THREE.SpriteMaterial;
          if (u <= 0) { m.opacity = 0; continue; }
          if (u < 1) {
            arc.at(u, p);
            const a = f.phase + u * f.spin;
            f.sp.position.set(p.x + arc.side.x * Math.cos(a) * f.r, p.y + Math.sin(a) * f.r * 0.7 + 0.4, p.z + arc.side.z * Math.cos(a) * f.r);
            m.opacity = Math.min(1, u * 6);
          } else {
            // at the far end the flakes scatter outward and settle
            const v = Math.min(1, (u - 1) * 2.5);
            const a = f.phase * 3;
            f.sp.position.set(to.x + Math.cos(a) * (1.5 + v * 5) * f.r * 0.5, 1 + (1 - v) * 1.5, to.z + Math.sin(a) * (1.5 + v * 5) * f.r * 0.5);
            m.opacity = 1 - v;
          }
          f.sp.scale.set(f.size, f.size, 1);
        }
      },
    });
  }

  // ---------------- Overtime embers ----------------

  /** How strongly the Overtime embers show: 0 = none, 1 = full. */
  setMood(mood: number, color: string, table: { width: number; depth: number }) {
    this.mood = mood;
    if (mood > 0 && !this.embers) {
      const pos = new Float32Array(EMBERS * 3);
      const col = new Float32Array(EMBERS * 3);
      for (let i = 0; i < EMBERS; i++) {
        // most embers rise from just outside the table's edge; a few drift through the room behind it
        let x: number;
        let z: number;
        if (i % 4 === 0) {
          const a = Math.random() * Math.PI * 2;
          const r = 120 + Math.random() * 110;
          x = Math.cos(a) * r;
          z = Math.sin(a) * r * 0.8 - 40;
        } else {
          const along = Math.random() * 2 - 1;
          const out = 3 + Math.random() * 12;
          const edge = Math.floor(Math.random() * 4);
          x = edge < 2 ? along * (table.width / 2 + out) : (edge === 2 ? -1 : 1) * (table.width / 2 + out);
          z = edge < 2 ? (edge === 0 ? -1 : 1) * (table.depth / 2 + out) : along * (table.depth / 2 + out);
        }
        this.emberSeeds.push({ x, z, speed: 3 + Math.random() * 6, sway: 1 + Math.random() * 3, phase: Math.random() * 100, top: 26 + Math.random() * 34 });
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
      this.embers = new THREE.Points(
        geo,
        new THREE.PointsMaterial({ map: fxTextures().dot, size: 2.4, sizeAttenuation: true, vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
      );
      this.embers.frustumCulled = false;
      this.emberColor.set(color);
      this.scene.add(this.embers);
    }
    if (this.embers) this.embers.visible = mood > 0.002;
  }
  private emberColor = new THREE.Color('#ff7a2e');

  // ---------------- every frame ----------------

  update(time: number) {
    textures().chevrons.offset.x = -(time * 1.7) % 1;
    this.preview?.update(time);
    this.arrows = this.arrows.filter((a) => {
      if (a.update(time)) return true;
      a.dispose();
      return false;
    });
    this.timed = this.timed.filter((m) => {
      const k = (time - m.born) / m.life;
      if (k < 1) {
        m.update(Math.max(0, k), time);
        return true;
      }
      this.remove(m.group);
      return false;
    });
    if (this.embers?.visible) {
      const pos = this.embers.geometry.attributes.position as THREE.BufferAttribute;
      const col = this.embers.geometry.attributes.color as THREE.BufferAttribute;
      const c = this.emberColor;
      this.emberSeeds.forEach((s, i) => {
        const u = ((time * s.speed) / s.top + s.phase) % 1; // 0 = just lit, 1 = burnt out
        const flicker = 0.75 + 0.25 * Math.sin(time * 9 + s.phase * 7);
        const glow = Math.sin(Math.PI * u) ** 0.7 * flicker * this.mood;
        pos.setXYZ(i, s.x + Math.sin(time * 0.6 + s.phase) * s.sway, -6 + u * s.top, s.z + Math.cos(time * 0.5 + s.phase) * s.sway);
        col.setXYZ(i, c.r * glow, c.g * glow * (1 - 0.35 * u), c.b * glow);
      });
      pos.needsUpdate = true;
      col.needsUpdate = true;
    }
  }

  private remove(group: THREE.Group) {
    this.scene.remove(group);
    group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      } else if (o instanceof THREE.Sprite) o.material.dispose();
    });
  }

  dispose() {
    this.setPreview(null);
    for (const a of this.arrows) a.dispose();
    for (const m of this.timed) this.remove(m.group);
    this.arrows = [];
    this.timed = [];
    if (this.embers) {
      this.scene.remove(this.embers);
      this.embers.geometry.dispose();
      (this.embers.material as THREE.Material).dispose();
      this.embers = null;
    }
  }
}
